import { useMemo } from 'react'
import { AppliedCoupon } from '../../../shared/utils/couponHelpers'
import { ShippingOption } from '../../../types/api'
import { getEffectiveUnitPrice } from '../../../constants/checkout'

export interface CheckoutCartItem {
  product: { price: number; weight?: number; allowsScrapReturn?: boolean }
  quantity: number
  willReturnScrap?: boolean
}

/** Retorna a preferência efetiva de sucata do item (item > carrinho > true). */
export function getItemWillReturnScrap(
  item: CheckoutCartItem,
  scrapPreference: boolean | null
): boolean {
  return item.willReturnScrap ?? scrapPreference ?? true
}

interface UseCheckoutCalculationsProps {
  appliedCoupons: AppliedCoupon[]
  selectedShippingOption: string | null
  shippingOptions: ShippingOption[]
  isLoadingShipping: boolean
  cartItems: CheckoutCartItem[]
  isCampinas: boolean
  scrapPreference: boolean | null
}

export const useCheckoutCalculations = ({
  appliedCoupons,
  selectedShippingOption,
  shippingOptions,
  isLoadingShipping,
  cartItems,
  isCampinas,
  scrapPreference
}: UseCheckoutCalculationsProps) => {
  const calculateSubtotal = useMemo(() => {
    return () => {
      return cartItems.reduce((sum, item) => {
        const allowsScrap = item.product.allowsScrapReturn === true
        const willReturn = getItemWillReturnScrap(item, scrapPreference)
        const unitPrice = allowsScrap
          ? getEffectiveUnitPrice(
              item.product.price,
              item.product.weight ?? 0,
              isCampinas,
              willReturn
            )
          : item.product.price
        return sum + unitPrice * item.quantity
      }, 0)
    }
  }, [cartItems, isCampinas, scrapPreference])

  const calculateShipping = useMemo(() => {
    return () => {
      // Retirada na loja é sempre grátis
      if (selectedShippingOption === 'pickup') return 0

      // Verificar se tem cupom de frete grátis
      if (appliedCoupons.some(c => c.type === 'free_shipping')) return 0

      // Se tiver opções de frete calculadas, usar o valor selecionado
      if (selectedShippingOption && shippingOptions.length > 0) {
        const selectedOption = shippingOptions.find(opt => opt.method === selectedShippingOption)
        if (selectedOption) {
          return selectedOption.price
        }
      }

      // Se ainda está calculando, retornar 0 (não mostrar valor fixo)
      if (isLoadingShipping) return 0

      // Se não tem opções calculadas, retornar 0 (não usar valores fixos)
      return 0
    }
  }, [appliedCoupons, selectedShippingOption, shippingOptions, isLoadingShipping])

  const calculateDiscount = useMemo(() => {
    return () => {
      const subtotal = calculateSubtotal()
      const shipping = calculateShipping()
      const totalBeforeDiscount = subtotal + shipping

      return appliedCoupons.reduce((total, coupon) => {
        // Para cupons fixos, ignorar o discountAmount do backend (que está limitado ao subtotal)
        // e recalcular baseado no total (subtotal + frete)
        if (coupon.type === 'fixed') {
          if (coupon.minOrderValue && subtotal < coupon.minOrderValue) {
            return total
          }
          // Desconto fixo pode ser aplicado ao total (subtotal + frete), não apenas ao subtotal
          return total + Math.min(coupon.value, totalBeforeDiscount)
        }

        // Para outros tipos de cupom, usar o discountAmount do backend se disponível
        if (typeof coupon.discountAmount === 'number') {
          return total + coupon.discountAmount
        }

        if (coupon.minOrderValue && subtotal < coupon.minOrderValue) {
          return total
        }

        switch (coupon.type) {
          case 'percentage': {
            const percentageDiscount = (subtotal * coupon.value) / 100
            return total + (coupon.maxDiscount ? Math.min(percentageDiscount, coupon.maxDiscount) : percentageDiscount)
          }
          default:
            return total
        }
      }, 0)
    }
  }, [appliedCoupons, calculateSubtotal, calculateShipping])

  const calculateTotal = useMemo(() => {
    return () => {
      const subtotal = calculateSubtotal()
      const shipping = calculateShipping()
      const discount = calculateDiscount()

      return Math.max(subtotal + shipping - discount, 0)
    }
  }, [calculateSubtotal, calculateShipping, calculateDiscount])

  return {
    calculateSubtotal,
    calculateShipping,
    calculateDiscount,
    calculateTotal
  }
}

